import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'whatwg-fetch';
import 'url-search-params-polyfill';
import { Buffer } from 'buffer';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './BaseComponents/Rollbar/rollbarconfig';
import {
  ApolloClient,
  ApolloClientOptions,
  InMemoryCache,
  NormalizedCacheObject,
  ApolloLink,
  HttpLink,
  ApolloProvider,
} from '@apollo/client';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import './index.css';
import possibleTypes from './Schema/SchemaInfo/possibleTypes.json';
import Aveny from './Features/Aveny';
import { onError } from '@apollo/client/link/error';
import Logger, { getErrorEnvironment, namedError } from './Utils/Logger';
import { isLoggedInError } from './Components/ServerError/ServerError';
import { getServerURL } from './Utils/getServerURL';
import { getGraphqlErrorExtraInfo } from './Utils/Error';
import LoginStateQuery from './Features/Login/Contexts/LoginStateContext/Queries/LoginStateQuery.gql';
import { StyledEngineProvider } from '@mui/material';
import ThemeProvider from './BaseComponents/ThemeProvider/EDThemeProvider';
import { LicenseInfo } from '@mui/x-license';

import { createHashRouter, RouterProvider } from 'react-router';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { complement, isEmpty, max, repeat } from 'ramda';
import {
  BaseRouteContext,
  BaseRouteValueContext,
  currentRoutingVersion,
  useBaseRouteParams,
  v0routeNames,
} from './Features/Routing/Utils/routerUtils';
import EnhancedSingleSignOn from './Components/SingleSignOn/SingleSignOn';
import { InternalError } from './Components/InternalError/InternalError';
import { V0RouteHandler } from './Features/Routing/OldRoutingHandlers/V0RouteHandler';
import { GlobalBreakpointContextProvider } from './Components/BreakpointContext/GlobalBreakpointContextProvider';
import { GlobalBusinessConfigContextProvider } from './Components/BusinessConfigContext/BusinessConfigContext';
import { RollbarProvider } from './BaseComponents/Rollbar/RollbarProvider';
import { RollbarIdProvider } from './Components/InternalError/RollbarIdContext';
import { setRollbarDatabase } from './Utils/RollbarUtils';
import { EDGlobalCookieContextProvider } from './Components/CookieSettings/GlobalCookieSettingsProvider';
import { newPasswordRouteName } from './Features/Routing/router';
import { NewPasswordPage } from './Features/Login/Components/NewPasswordPage/NewPasswordPage';
import { httpStatusCodeName } from './Utils/httpStatusCodes';
import './BaseComponents/GlobalStateHandler/GlobalStateHandler';
import { useVersionMismatch } from './Features/VersionCheck/useVersionMismatch';
import VersionMismatchModal from './Features/VersionCheck/VersionMismatchModal';

// Registera licens för MUI-X pro
LicenseInfo.setLicenseKey(
  '48df3721104f21583f87262df69718dfTz0xMTk2NDMsRT0xNzkwNDY3MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
);

// används av base64url (Buffer finns i node)
globalThis.Buffer = Buffer;

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies: {
    TEDAvenyDBQuery: {
      merge: true,
    },
    TEDAvenyRoutineQuery: {
      merge: true,
    },
    TEDAvenyUserInfo: {
      merge: true,
      keyFields: (_object, _context) => {
        return 'OverriddenTEDAvenyUserInfo';
      },
    },
    TEDLRLeverantorAssociations: {
      fields: {
        Fakturor_Connection: {
          keyArgs: ['first', 'order', 'selection'],
          merge(existing, incoming) {
            const exEdges = existing?.edges ?? [];
            const inEdges = incoming?.edges ?? [];

            const isOldValues = exEdges.some((edge: any) => {
              return inEdges.some(
                (inEdge: any) => inEdge.cursor === edge.cursor
              );
            });

            if (isOldValues) {
              return {
                __typename: 'TEDLRLeverantorfakturaConnection',
                edges: exEdges,
                totalCount: incoming?.totalCount ?? 0,
              };
            }

            return {
              __typename: 'TEDLRLeverantorfakturaConnection',
              edges: [...exEdges, ...inEdges],
              totalCount: incoming?.totalCount ?? 0,
            };
          },
        },
      },
    },
  },
});

const httpLink = new HttpLink({
  uri: `${getServerURL()}/graphql`,
  credentials: 'include',
  /** Behövs för att debugInfo ska komma med i requestet */
  includeUnusedVariables: true,
});

const errorLink = onError(error => {
  const { graphQLErrors, networkError, operation } = error;
  if (graphQLErrors) {
    graphQLErrors.map(graphQLError => {
      if (isLoggedInError(graphQLError)) {
        // vi loggar inte till Rollbar, så för ev. felsökning så loggar vi till console
        // eslint-disable-next-line no-console
        console.log(
          `Updating login state after ${graphQLError.extensions?.ExceptionClass} (${graphQLError.message})`
        );
        client.query({
          query: LoginStateQuery,
          fetchPolicy: 'network-only',
        });
      } else {
        const extraInfo = getGraphqlErrorExtraInfo(graphQLError, operation);
        const error = Error(`Graphql error: ${graphQLError.message}`);
        error.name = extraInfo.name;
        // Logger.warn(error, extraInfo);
        Logger.log(
          'warning',
          'ServererrorWarning',
          'Serverfel ' + extraInfo.name,
          error,
          {
            errorMessage: graphQLError.message,
            ...extraInfo,
          },
          {
            fingerprint: 'ServerErrorWarning' + extraInfo.name,
            reporter: 'errorlink',
          }
        );
      }
    });
  }
  if (networkError) {
    let statusCode: number | undefined = undefined;
    if ('statusCode' in networkError) {
      statusCode = networkError.statusCode;
    }

    const errorName = `NetworkError${statusCode ? statusCode : ''}`;
    const errorMessage = `Nätverksfel - ${
      statusCode ? httpStatusCodeName(statusCode) : 'statuskod saknas'
    } - ${networkError.message}`;
    const error = namedError(errorName, errorMessage, networkError);

    Logger.log(
      'error',
      errorName,
      errorMessage,
      error,
      {
        operationName: operation.operationName,
        networkErrorName: networkError.name,
        networkErrorMessage: networkError.message,
      },
      {
        fingerprint: errorName,
        reporter: 'errorlink',
      }
    );
  }
});

const debugInfoLink = new ApolloLink((operation, forward) => {
  operation.variables = {
    ...operation.variables,
    debugInfo: { routineName: getErrorEnvironment().rutin },
  };

  return forward(operation);
});

// Lägg ihop länkarna
const apolloLink = ApolloLink.from([debugInfoLink, errorLink, httpLink]);

const options = {
  cache,
  link: apolloLink,
} as ApolloClientOptions<NormalizedCacheObject>;

const client = new ApolloClient(options);
const addMissingRouteParts = (location: {
  pathname: string;
  search: string;
}): { pathname: string; search: string } => {
  const pathnameParts = location.pathname
    .split('/')
    .filter(complement(isEmpty));
  const missingParts = repeat<string>('_', max(0, 4 - pathnameParts.length));
  const newPathname = pathnameParts.concat(missingParts).join('/');
  return { pathname: newPathname, search: location.search };
};

const BaseRouteHandler: React.FC<React.PropsWithChildren<{}>> = () => {
  const params = useParams();
  const remainingPath = params['*'] ?? '';
  const atStart = remainingPath === '';
  const atLogin = remainingPath === 'Login';
  const baseRoute = useBaseRouteParams();
  const databaseName: string = baseRoute[0].database || 'databas saknas';
  useEffect(() => {
    setRollbarDatabase(databaseName);
  }, [databaseName]);
  return (
    <BaseRouteContext.Provider value={baseRoute}>
      <BaseRouteValueContext.Provider value={baseRoute[0]}>
        <GlobalBusinessConfigContextProvider>
          <Aveny atStart={atStart} atLogin={atLogin} />
        </GlobalBusinessConfigContextProvider>
      </BaseRouteValueContext.Provider>
    </BaseRouteContext.Provider>
  );
};
const RoutingHandler: React.FC<React.PropsWithChildren<{}>> = () => {
  const { routingVersionPath } = useParams();
  //Hantera aktuell version
  if (routingVersionPath === currentRoutingVersion) {
    return (
      <Routes>
        <Route
          path=":systemPath/:usernamePath/:databasePath/*"
          element={<BaseRouteHandler />}
        />
      </Routes>
    );
  }
  //Redirecta till en uppdaterad path från version 0
  if (v0routeNames.includes(routingVersionPath ?? '')) {
    return <V0RouteHandler />;
  }
  return (
    <InternalError
      message={`Sökvägen med version "${routingVersionPath}" kan inte hanteras`}
      id="388c08bf-3263-4bbf-a4e7-dd555849dbe6"
    />
  );
};
interface IVersionMismatchGateProps {
  children: React.ReactNode;
}
/**
 * Gate component that must be rendered inside ApolloProvider to use Apollo hooks.
 * It blocks rendering of children until a version check has completed.
 */
const VersionMismatchGate: React.FC<IVersionMismatchGateProps> = ({
  children,
}) => {
  const { mismatch, backendVersion, frontendVersion, loading } =
    useVersionMismatch();
  // While loading: render nothing (avoid flashing UI before potential block)
  if (loading) return null;
  // If mismatch: show only the blocking modal (children are not mounted)
  if (mismatch) {
    return (
      <VersionMismatchModal
        open={true}
        backendVersion={backendVersion}
        frontendVersion={frontendVersion}
      />
    );
  }
  return <>{children}</>;
};

const CatchAllRedirect: React.FC = () => {
  const location = useLocation();
  const to = addMissingRouteParts(location);
  return <Navigate to={to} replace />;
};

const router = createHashRouter([
  {
    path: '/',
    element: <Navigate to={`/${currentRoutingVersion}/Lokalt/_/_`} replace />,
  },
  {
    path: '/login',
    element: (
      <Navigate to={`/${currentRoutingVersion}/Lokalt/_/_/login`} replace />
    ),
  },
  {
    path: '/singlesignon/*',
    element: <EnhancedSingleSignOn />,
  },
  {
    path: `/${newPasswordRouteName}/*`,
    element: <NewPasswordPage />,
  },
  {
    path: ':routingVersionPath/*',
    element: <RoutingHandler />,
  },
  {
    path: '*',
    element: <CatchAllRedirect />,
  },
]);

const App: React.FC<React.PropsWithChildren<unknown>> = () => (
  <EDGlobalCookieContextProvider>
    <RollbarProvider>
      <RollbarIdProvider>
        <ApolloProvider client={client}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider>
              <GlobalBreakpointContextProvider>
                <VersionMismatchGate>
                  <RouterProvider router={router} />
                </VersionMismatchGate>
              </GlobalBreakpointContextProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </ApolloProvider>
      </RollbarIdProvider>
    </RollbarProvider>
  </EDGlobalCookieContextProvider>
);

const container = document.getElementById('root');
const root = createRoot(container!); // createRoot(container!) if you use TypeScript
root.render(<App />);
